import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { useEffect, useState } from 'react';

import { useCaptureError } from '@/lib/posthog/hooks/useCaptureError';

const DISCONNECTED_CONNECTION_STATES = [
  HubConnectionState.Disconnected,
  HubConnectionState.Disconnecting,
];

const RETRY_DELAYS = [0, 2000, 10000, 30000, 60000];

// `withAutomaticReconnect` only retries after a connection has been established
// and then dropped — it never retries a failed initial `start()`. Transient
// negotiation hiccups (a network `Load failed`, or a proxy/deploy-window 404)
// therefore surfaced as captured exceptions on the very first attempt. Retry the
// initial connection ourselves with backoff, and only report a failure once
// every attempt has been exhausted.
const START_RETRY_DELAYS = [2000, 10000, 30000];

export const useSignalRConnection = ({ hubUrl }: { hubUrl: string }) => {
  const [connection, setConnection] = useState<HubConnection>();

  const captureError = useCaptureError();

  useEffect(() => {
    const newConnection = new HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect(RETRY_DELAYS)
      .configureLogging(LogLevel.Information)
      .build();

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setConnection(newConnection);
  }, [hubUrl]);

  useEffect(() => {
    if (!connection) return;

    let cancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;

    const startConnection = async (attempt = 0) => {
      if (cancelled) return;
      if (!DISCONNECTED_CONNECTION_STATES.includes(connection.state)) return;

      try {
        await connection.start();
      } catch (err) {
        if (cancelled) return;

        const nextDelay = START_RETRY_DELAYS[attempt];
        if (nextDelay === undefined) {
          // Every retry has been exhausted — the hub is genuinely unreachable,
          // so this is worth reporting rather than a transient first-attempt blip.
          captureError(err, {
            errorType: 'signalr-start-connection-failed',
          });
          return;
        }

        retryTimeout = setTimeout(
          () => startConnection(attempt + 1),
          nextDelay,
        );
      }
    };

    connection.onclose((error) => {
      if (error) {
        captureError(error, {
          errorType: 'signalr-close-connection-failed',
        });
      }
    });

    startConnection();

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);

      if (!DISCONNECTED_CONNECTION_STATES.includes(connection.state)) {
        connection.stop().catch((err) => {
          captureError(err, {
            errorType: 'signalr-stop-connection-failed',
          });
        });
      }
    };
  }, [connection, captureError]);

  return connection;
};
